import type { Mandate } from "@/types/mandate";
import type { InstallmentStatus } from "@/types/installment";

const COLLECTED: InstallmentStatus[] = ["COLLECTED", "SETTLEMENT_PENDING", "SETTLED", "SETTLEMENT_FAILED"];

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function successBarColor(rate: number): string {
  if (rate > 75) return "#059669";
  if (rate >= 60) return "#D97706";
  return "#DC2626";
}

export function inRange(m: Mandate, range: string, from?: string, to?: string): boolean {
  const d = new Date(m.createdAt).getTime();
  const now = new Date();
  switch (range) {
    case "Today": {
      const key = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
      return key(new Date(m.createdAt)) === key(now);
    }
    case "Last 7 Days":
      return d >= new Date(now.getTime() - 7 * 86400000).getTime();
    case "Last 30 Days":
      return d >= new Date(now.getTime() - 30 * 86400000).getTime();
    case "This Month":
      return d >= new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    case "Last 3 Months":
      return d >= new Date(now.getTime() - 90 * 86400000).getTime();
    case "Custom Range": {
      if (from && to) {
        const fromD = new Date(from).getTime();
        const toD = new Date(to).getTime() + 86400000;
        return d >= fromD && d <= toD;
      }
      return true;
    }
    default:
      return true;
  }
}

export interface RetailerRow {
  id: string;
  name: string;
  shopName: string;
  total: number;
  activated: number;
  pending: number;
  failedRejected: number;
  totalValue: number;
  successRate: number;
}

export function retailerRows(list: Mandate[], liveRetailers?: any[]): RetailerRow[] {
  const counts = new Map<
    string,
    { total: number; activated: number; pending: number; failedRejected: number; value: number }
  >();
  list.forEach((m) => {
    const cur = counts.get(m.retailerId) ?? {
      total: 0,
      activated: 0,
      pending: 0,
      failedRejected: 0,
      value: 0,
    };
    cur.total += 1;
    if (m.status === "ACTIVE") {
      cur.activated += 1;
      cur.value += m.amount;
    }
    if (m.status === "PENDING_AUTH") cur.pending += 1;
    if (m.status === "TERMINATED" || m.status === "HALTED") cur.failedRejected += 1;
    counts.set(m.retailerId, cur);
  });

  const baseRetailers = (liveRetailers && liveRetailers.length > 0) ? liveRetailers : [];

  return baseRetailers
    .map((r) => {
      const c = counts.get(r.id) ?? { total: 0, activated: 0, pending: 0, failedRejected: 0, value: 0 };
      return {
        id: r.id,
        name: r.name,
        shopName: r.shopName || r.name,
        total: c.total,
        activated: c.activated,
        pending: c.pending,
        failedRejected: c.failedRejected,
        totalValue: c.value,
        successRate: c.total ? Math.round((c.activated / c.total) * 100) : 0,
      };
    })
    .sort((a, b) => b.total - a.total);
}

export interface BankRow {
  bank: string;
  total: number;
  activated: number;
  successRate: number;
}

export function bankRows(list: Mandate[]): BankRow[] {
  const map = new Map<string, { total: number; activated: number }>();
  list.forEach((m) => {
    const bankKey = m.bankName || "Other Bank";
    const cur = map.get(bankKey) ?? { total: 0, activated: 0 };
    cur.total += 1;
    if (m.status === "ACTIVE") cur.activated += 1;
    map.set(bankKey, cur);
  });
  return Array.from(map.entries())
    .map(([bank, c]) => ({
      bank,
      total: c.total,
      activated: c.activated,
      successRate: c.total ? Math.round((c.activated / c.total) * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

export interface ModeRow {
  mode: string;
  total: number;
  activated: number;
  failed: number;
  rejected: number;
  successRate: number;
  avgAmount: number;
}

export function modeRows(list: Mandate[]): ModeRow[] {
  const map = new Map<
    string,
    { total: number; activated: number; failed: number; rejected: number; amount: number }
  >();
  list.forEach((m) => {
    const modeKey = m.authMode || "NACH";
    const cur = map.get(modeKey) ?? { total: 0, activated: 0, failed: 0, rejected: 0, amount: 0 };
    cur.total += 1;
    cur.amount += m.amount;
    if (m.status === "ACTIVE") cur.activated += 1;
    if (m.status === "TERMINATED") cur.failed += 1;
    if (m.status === "HALTED") cur.rejected += 1;
    map.set(modeKey, cur);
  });
  return Array.from(map.entries())
    .map(([mode, c]) => ({
      mode,
      total: c.total,
      activated: c.activated,
      failed: c.failed,
      rejected: c.rejected,
      successRate: c.total ? Math.round((c.activated / c.total) * 100) : 0,
      avgAmount: c.total ? Math.round(c.amount / c.total) : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

export interface MonthRow {
  label: string;
  total: number;
  activated: number;
  failed: number;
  rejected: number;
  successRate: number;
}

export function monthLabel(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function monthlyRows(list: Mandate[], months: string[]): MonthRow[] {
  const byKey = new Map<string, { total: number; activated: number; failed: number; rejected: number }>();
  list.forEach((m) => {
    const key = monthLabel(new Date(m.createdAt));
    const cur = byKey.get(key) ?? { total: 0, activated: 0, failed: 0, rejected: 0 };
    cur.total += 1;
    if (m.status === "ACTIVE") cur.activated += 1;
    if (m.status === "TERMINATED") cur.failed += 1;
    if (m.status === "HALTED") cur.rejected += 1;
    byKey.set(key, cur);
  });
  return months.map((label) => {
    const c = byKey.get(label) ?? { total: 0, activated: 0, failed: 0, rejected: 0 };
    return {
      label,
      ...c,
      successRate: c.total ? Math.round((c.activated / c.total) * 100) : 0,
    };
  });
}

export interface CollectionRow {
  id: string;
  name: string;
  shopName: string;
  total: number;
  collected: number;
  offline: number;
  failed: number;
  amountCollected: number;
  rate: number;
  charges: number;
}

export function collectionRows(liveInstallments?: any[], liveRetailers?: any[]): CollectionRow[] {
  const counts = new Map<
    string,
    { total: number; collected: number; offline: number; failed: number; amount: number; charges: number }
  >();
  const baseInstallments = (liveInstallments && liveInstallments.length > 0) ? liveInstallments : [];
  const baseRetailers = (liveRetailers && liveRetailers.length > 0) ? liveRetailers : [];

  baseInstallments.forEach((t: any) => {
    const cur = counts.get(t.retailerId) ?? { total: 0, collected: 0, offline: 0, failed: 0, amount: 0, charges: 0 };
    cur.total += 1;
    if (t.status === "COLLECTED" || t.status === "SETTLEMENT_PENDING" || t.status === "SETTLED" || t.status === "SETTLEMENT_FAILED") {
      cur.collected += 1;
      cur.amount += Number(t.amount || 0);
      cur.charges += Number(t.chargeAmount || t.platformCharge || 0);
    }
    if (t.status === "COLLECTED_OFFLINE") {
      cur.offline += 1;
      cur.amount += Number(t.amount || 0);
    }
    if (t.status === "FAILED" || t.status === "SETTLEMENT_FAILED") cur.failed += 1;
    counts.set(t.retailerId, cur);
  });
  return baseRetailers.map((r: any) => {
    const c = counts.get(r.id) ?? { total: 0, collected: 0, offline: 0, failed: 0, amount: 0, charges: 0 };
    return {
      id: r.id,
      name: r.name,
      shopName: r.shopName || r.name,
      total: c.total,
      collected: c.collected,
      offline: c.offline,
      failed: c.failed,
      amountCollected: c.amount,
      rate: c.total ? Math.round(((c.collected + c.offline) / c.total) * 100) : 0,
      charges: c.charges,
    };
  });
}

export function collectionRateColor(rate: number): string {
  if (rate > 90) return "#059669";
  if (rate >= 75) return "#D97706";
  return "#DC2626";
}